import { abortNavigation, defineNuxtRouteMiddleware, useRouter } from '#app/composables/router';
import { useNuxtApp } from '#app/nuxt';
import { useAuth } from '#layers/access/public';
import { workspaceRuntime, WorkspaceSwitchSupersededError } from '#layers/runtime/public';
import { useToastResultHandling } from '#layers/ui/public';
import { rememberWorkspace, toWorkspaceSwitchError } from '~/workspace/switch-errors';
import { createWorkspaceSwitchMiddleware } from '~/workspace/switch-middleware';
import { useWorkspaceSwitch } from '~/workspace/use-workspace-switch';

/** Nuxt adapter: 403 normalization, the failure toast and last-workspace persistence stay out of the guard core. */
export default defineNuxtRouteMiddleware(createWorkspaceSwitchMiddleware({
	abortNavigation,
	resolveDependencies() {
		const auth = useAuth();
		const { switchTo } = useWorkspaceSwitch();
		const { t } = useNuxtApp().$i18n;
		const router = useRouter();
		const { onError } = useToastResultHandling();

		async function switchWithFeedback(workspaceSlug: string) {
			try {
				await switchTo(workspaceSlug);
			}
			catch (error) {
				if (error instanceof WorkspaceSwitchSupersededError) throw error;

				const visibleError = toWorkspaceSwitchError({ error, noAccessMessage: t('workspace.no_access') });

				// Without a published workspace there is nothing to stay on: surface the error page.
				if (workspaceRuntime.activeRuntime.value === null) throw visibleError;

				onError(visibleError, { duration: 0 });
				return 'kept' as const;
			}

			rememberWorkspace({ userId: auth.currentUser?.id, workspaceSlug });
			return 'switched' as const;
		}

		return {
			switchTo: switchWithFeedback,
			resolve: (fullPath) => router.resolve(fullPath),
		};
	},
}));
